// apps/buildlog/src/pages/McpLogin.tsx
//
// OAuth login page — shown when Claude initiates the Buildlog MCP connector flow.
// User signs in with Google, we exchange the ID token for an MCP token,
// then redirect back to Claude with the auth code.
//
// Route: /mcp/login?session=<sessionId>

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { signInWithPopup, GoogleAuthProvider, getAuth } from 'firebase/auth';
import { Fingerprint } from 'lucide-react';

const AUTH_CALLBACK_URL = 'https://buildlogapi-x2efogv6ja-uc.a.run.app/mcp/auth/callback';

export function McpLogin() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session') ?? '';

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleGoogleSignIn = async () => {
    if (!sessionId) {
      setError('No session found. Please start the connection from Claude\'s connector settings and try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const auth     = getAuth();
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      const result  = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();

      const resp = await fetch(AUTH_CALLBACK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, idToken }),
      });

      if (!resp.ok) {
        const data = await resp.json() as { error?: string };
        throw new Error(data.error ?? 'Something went wrong. Please try again.');
      }

      const { redirectUrl } = await resp.json() as { redirectUrl: string };
      window.location.href = redirectUrl;

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-10 w-full max-w-sm">

        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <Fingerprint size={18} className="text-white opacity-60" />
          <span className="font-bold text-[15px] tracking-tight text-white opacity-60">
            Buildlog
          </span>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white leading-snug mb-2">
            Connect Buildlog to Claude
          </h1>
          <p className="text-[14px] text-slate-400 leading-relaxed">
            Sign in to give Claude access to your projects and build logs. Once connected, Claude can read your entries and save new ones on your behalf.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-3 bg-red-950 border border-red-800 rounded-lg text-sm text-red-400">
            {error}
          </div>
        )}

        {/* No session warning */}
        {!sessionId && (
          <div className="mb-6 p-3 bg-amber-950 border border-amber-800 rounded-lg text-sm text-amber-400">
            No session found. Start the connection from Claude's connector settings and come back here.
          </div>
        )}

        {/* Sign in button */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading || !sessionId}
          className="w-full py-3 px-4 bg-white hover:bg-slate-100 disabled:bg-slate-800 disabled:text-slate-600 text-slate-900 font-semibold rounded-xl transition-colors flex items-center justify-center gap-3"
        >
          {loading ? (
            <span className="inline-block w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          )}
          {loading ? 'Connecting…' : 'Continue with Google'}
        </button>

        {/* Trust footer */}
        <p className="text-xs text-slate-600 mt-6 text-center leading-relaxed">
          Claude will be able to read and write your Buildlog entries.
          You can disconnect at any time from Buildlog settings.
        </p>

      </div>
    </div>
  );
}